/**
 * Which app-lock surface the nurse should be looking at.
 *
 * Deliberately its own module with NO React Native imports, so the decision can
 * be unit-tested under plain Node. The ordering encoded here is the whole
 * safety argument for the rollout — an app that has not heard from the server,
 * or is talking to an API that predates this feature, must behave exactly as it
 * did before — and that argument should be provable, not asserted.
 *
 * See src/__tests__/app-lock-screen.test.ts.
 */

export type AppLockScreen =
    | "unlocked"
    | "locked"
    | "setup"
    | "account-ended"
    /** Cold start, before the local secure-store check has resolved. */
    | "initializing";

export interface AppLockScreenInput {
    accountEndedReason: "terminated" | "deactivated" | null;
    isAuthenticated: boolean;
    initializing: boolean;
    locked: boolean;
    hasPin: boolean;
    needsSetup: boolean;
    setupSkipped: boolean;
    /**
     * Null until the first successful session fetch — which also covers an
     * older API that has no such endpoint, and being offline.
     */
    policy: { enabled: boolean; required: boolean } | null;
}

/**
 * Order matters:
 *  - `account-ended` outranks everything; there is nothing to unlock into.
 *  - `initializing` outranks the app so a cold start never flashes real
 *    content before the lock resolves.
 *  - a device holding a PIN is always honoured, even if the feature is later
 *    switched off — silently opening an app the nurse believes is locked is
 *    worse than showing a feature slightly early.
 *  - setup requires the server to have positively said the feature is live.
 *    An absent policy means "show nothing", never "prompt anyway".
 */
export function resolveAppLockScreen(input: AppLockScreenInput): AppLockScreen {
    if (input.accountEndedReason) {
        return "account-ended";
    }
    if (!input.isAuthenticated) {
        return "unlocked";
    }
    if (input.initializing) {
        return "initializing";
    }
    if (input.locked && input.hasPin) {
        return "locked";
    }
    const featureLive = input.policy?.enabled === true;
    const mustEnroll = input.policy?.required === true;
    if (featureLive && input.needsSetup && (mustEnroll || !input.setupSkipped)) {
        return "setup";
    }
    return "unlocked";
}

/**
 * Whether the device's PIN should be wiped because nobody is signed in.
 *
 * Only once the auth restore has finished. On every cold start AuthProvider
 * renders first with `user = null` while it reads the saved session, so
 * "not authenticated" alone also matches the first render of a nurse who IS
 * signed in. Wiping on that deleted her PIN at every launch. Between 09-12 and
 * 09-24, 2,982 enrollments came in for 301 nurses, about ten each, and the lock
 * could be bypassed by force-quitting the app and choosing a new PIN.
 */
export function shouldWipeLocalLock(input: {
    isAuthLoading: boolean;
    isAuthenticated: boolean;
}): boolean {
    return !input.isAuthLoading && !input.isAuthenticated;
}
